import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import TuningPointsModal from '../components/TuningPointsModal';
import { getPhotoUrl } from '../utils/bucketUrl';

const TuningPoints = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const benefits = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      titleKey: 'tuningPoints.benefit1Title',
      descKey: 'tuningPoints.benefit1Desc',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      titleKey: 'tuningPoints.benefit2Title',
      descKey: 'tuningPoints.benefit2Desc',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      titleKey: 'tuningPoints.benefit3Title',
      descKey: 'tuningPoints.benefit3Desc',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      titleKey: 'tuningPoints.benefit4Title',
      descKey: 'tuningPoints.benefit4Desc',
    },
  ];

  const dailyTasks = [
    'tuningPoints.task1',
    'tuningPoints.task2',
    'tuningPoints.task3',
    'tuningPoints.task4',
  ];

  const targetAudience = [
    'tuningPoints.target1',
    'tuningPoints.target2',
    'tuningPoints.target3',
  ];

  return (
    <>
      <SEO
        title={t('tuningPoints.pageTitle')}
        description={t('tuningPoints.pageDescription')}
        keywords={t('seo.tuningPoints.keywords')}
        type="website"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              {t('tuningPoints.title')} <span className="text-racing-red">Meller Racing</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('tuningPoints.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-racing-black mb-4">
              {t('tuningPoints.benefitsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('tuningPoints.benefitsSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="text-racing-red mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-gray-600">
                  {t(benefit.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Tasks Section with Photo Background */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={getPhotoUrl('IMG_8825.jpeg')}
            alt="Tuning workshop"
            className="w-full h-full object-cover scale-70 brightness-75"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-racing-black/60 via-racing-black/35 to-racing-black/25"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-racing-black/45 via-transparent to-transparent"></div>
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <span className="px-6 py-3 bg-racing-red/20 backdrop-blur-sm border border-racing-red text-racing-red font-bold rounded-full text-sm tracking-wide uppercase inline-block mb-6">
                {t('tuningPoints.dailyTasksBadge')}
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6">
                {t('tuningPoints.dailyTasksTitle')}
              </h2>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                {t('tuningPoints.dailyTasksDesc')}
              </p>
            </motion.div>

            <ul className="space-y-6">
              {dailyTasks.map((taskKey, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group flex items-start bg-racing-black/60 backdrop-blur-md p-6 rounded-2xl border border-white/30 hover:bg-racing-black/70 hover:border-racing-red/60 transition-all duration-300 shadow-xl"
                >
                  <div className="flex-shrink-0 mr-5">
                    <div className="w-12 h-12 bg-gradient-to-br from-racing-red to-racing-darkRed rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 pt-1">
                    <span className="text-gray-100 text-lg font-semibold leading-relaxed group-hover:text-white transition-colors duration-300">
                      {t(taskKey)}
                    </span>
                  </div>
                </motion.li>
              ))}
            </ul>

            {/* Get More Details Button - Under Daily Tasks Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex justify-center mt-10"
            >
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-3 bg-racing-red/20 hover:bg-racing-red/30 border border-racing-red/60 text-racing-red rounded-lg font-semibold text-base transition-all duration-300 hover:scale-105"
              >
                {t('tuningPoints.moreDetailsButton')}
              </button>
            </motion.div>
          </motion.div>
        </div>

      </section>

      {/* Target Audience Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-racing-black mb-4">
              {t('tuningPoints.forWhomTitle')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {targetAudience.map((targetKey, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card text-center p-8"
              >
                <div className="w-16 h-16 bg-racing-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-racing-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 font-medium">
                  {t(targetKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
              {t('tuningPoints.ctaTitle')}
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {t('tuningPoints.ctaDescription')}
            </p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link 
                to="/partnership" 
                className="inline-block px-12 py-5 bg-racing-red hover:bg-racing-darkRed text-white font-bold text-xl rounded-lg transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
              >
                {t('tuningPoints.contactButton')}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Modal */}
      <TuningPointsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default TuningPoints;
